<i18n>
{
  "fr": {
    "edit-button-tooltip": "Modifier",
    "delete-button-tooltip": "Supprimer",
    "confirm-delete-title": "Supprimer le document",
    "confirm-delete-message": "Êtes-vous certain de vouloir supprimer le document «\u00A0{title}\u00A0»\u00A0?",
    "confirm-delete-cancel": "Annuler",
    "confirm-delete-confirm": "Supprimer",
    "edit-title": "Modifier le document",
    "edit-submit": "Enregistrer",
    "link-missing": "Lien manquant",
    "draghandle-tooltip": "Réordonner le document",
    "completed-at": "Complété le",
    "last-update":"Dernière mise à jour",
    "by": "Par"
  },
  "en": {
    "edit-button-tooltip": "Edit",
    "delete-button-tooltip": "Delete",
    "confirm-delete-title": "Delete document",
    "confirm-delete-message": "Are you sure you want to delete the document \"{title}\"?",
    "confirm-delete-cancel": "Cancel",
    "confirm-delete-confirm": "Delete",
    "edit-title": "Edit document",
    "edit-submit": "Save",
    "link-missing": "Missing link",
    "draghandle-tooltip": "Reorder document",
    "completed-at": "Completed on",
    "last-update":"Last update",
    "by": "By"
  }
}
</i18n>

<template>
  <div>
    <div v-if="!editing" class="card is-shadowless document" style="border: 1px solid #d0d8e5">
      <div
        class="py-12px document-head"
        :class="{
          'px-12px': $media.mobile,
          'px-24px': $media.tablet
        }"
      >
        <div class="level">
          <div class="level-left pr-8px" style="flex-shrink: 1">
            <div class="pl-24px is-relative">
              <input
                type="checkbox"
                :checked="doc.isDone"
                @click="(ev) => $emit('onChangeChecked', ev)"
                style="position: absolute; top: 0.5em; left: 0; cursor: pointer"
                :disabled="readOnly"
              />
              <p class="has-text-weight-bold mb-4px">
                {{ doc.title }}
              </p>
              <div v-if="doc.completedAt" class="is-size-7">{{ $t('completed-at') }} {{ $format.dateTime(doc.completedAt) }}</div>
              <div v-if="doc.completedBy" class="is-size-7">
                {{ $t('by') }} {{ doc.completedBy.profile.firstName }} {{ doc.completedBy.profile.lastName }}
              </div>
            </div>
          </div>
          <div class="level-right is-flex">
            <div class="field has-addons m-0">
              <div class="control">
                <b-tooltip
                  :label="$t('edit-button-tooltip')"
                  type="is-light"
                  size="is-small"
                  :position="$media.tablet ? 'is-left' : 'is-right'"
                >
                  <button class="button is-small is-outlined" @click="toggleEdit" :disabled="readOnly">
                    <span class="icon is-small has-text-link"><i class="fas fa-pen" /></span>
                  </button>
                </b-tooltip>
              </div>
              <div class="control">
                <b-tooltip
                  :label="$t('delete-button-tooltip')"
                  type="is-light"
                  size="is-small"
                  :position="$media.tablet ? 'is-left' : 'is-right'"
                >
                  <button class="button is-small is-outlined" @click="confirmDelete" :disabled="readOnly">
                    <span class="icon is-small has-text-link"><i class="far fa-trash-alt" /></span>
                  </button>
                </b-tooltip>
              </div>
            </div>
            <b-tooltip
              v-if="dragHandle"
              :label="$t('draghandle-tooltip')"
              type="is-light"
              size="is-small"
              :position="$media.tablet ? 'is-left' : 'is-right'"
              class="ml-2"
            >
              <b-icon icon="grip-lines" data-draghandle style="cursor: grab" />
            </b-tooltip>
          </div>
        </div>
      </div>
      <hr class="m-0" />
      <div
        v-if="(doc.content && doc.contentHtml.trim()) || doc.link || doc.isDone"
        class="py-12px"
        :class="{
          'px-12px': $media.mobile,
          'px-24px': $media.tablet
        }"
      >
        <div class="pl-24px is-relative">
          <p
            v-if="doc.content && doc.contentHtml.trim()"
            v-html="doc.contentHtml"
            :class="{
              'mb-12px': doc.link || doc.isDone
            }"
          ></p>
          <document-link v-if="doc.link" :link="doc.link" class="is-size-7" />
          <b-tag
            v-else-if="doc.isDone"
            type="is-danger is-uppercase"
            close-type="is-light"
            attached
            closable
            close-icon="pen"
            @close="toggleEdit"
            :disabled="readOnly"
          >
            {{ $t('link-missing') }}
          </b-tag>
        </div>
      </div>
      <div class="has-background-white-ter">
        <!-- Note de suivi -->
        <notes
          class="ml-24px py-12px"
          :class="{
            'px-12px': $media.mobile,
            'px-24px': $media.tablet
          }"
          :notes="doc.notes"
          :showForm="showForm"
        />
        <hr v-if="!showForm" class="m-0" />
        <div
          class="py-12px"
          :class="{
            'px-12px': $media.mobile,
            'px-24px': $media.tablet
          }"
        >
          <div class="is-relative document__footer ml-24px" :style="minHeight">
            <notes-form
              ref="notesForm"
              isAbsolutePos
              :notes="doc.notes"
              @onSaveNotes="(notes) => $emit('onSaveNotes', notes)"
              @toggleShowForm="toggleShowForm"
              :readOnly="readOnly"
            />
            <!-- Document modifié -->
            <div v-if="doc.updatedBy && !showForm" class="is-size-7">
              {{ $t('last-update') }} {{ doc.updatedBy.profile.firstName }} {{ doc.updatedBy.profile.lastName }}
              <span v-if="doc.updatedAt">- {{ $format.dateTime(doc.updatedAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <document-form
      v-if="editing"
      :document="doc"
      :title="$t('edit-title')"
      :submitLabel="$t('edit-submit')"
      @submit="(doc) => $emit('on-update', doc)"
      @cancel="toggleEdit()"
      class="card is-shadowless"
      style="border: 1px solid #d0d8e5"
      :class="{
        'p-12px': $media.mobile,
        'p-16px': $media.tablet,
        'p-24px': $media.desktop
      }"
    />
  </div>
</template>

<script>
import Notes from '@/components/generic/notes'
import NotesForm from '@/components/generic/notes-form'
import DocumentLink from '@/components/generic/document-link'
import DocumentForm from '@/components/indicator/document-form'
import Breakpoint from '@/mixins/breakpoints'

export default {
  mixins: [Breakpoint],
  components: {
    Notes,
    NotesForm,
    DocumentLink,
    DocumentForm
  },
  props: {
    doc: Object,
    editing: Boolean,
    dragHandle: Boolean,
    readOnly: Boolean
  },
  data() {
    return {
      showForm: false,
      footerMinHeight: 0
    }
  },
  computed: {
    minHeight() {
      if (!this.showForm || !this.$breakpoint.desktopAndUp) return 'minHeight: 0px;'
      return `minHeight: ${this.footerMinHeight}px;`
    }
  },
  methods: {
    confirmDelete() {
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-delete-title'),
        message: this.$t('confirm-delete-message', { title: this.doc.title }),
        cancelText: this.$t('confirm-delete-cancel'),
        confirmText: this.$t('confirm-delete-confirm'),
        type: 'is-danger',
        onConfirm: () => this.$emit('on-delete', this.doc)
      })
    },
    toggleEdit() {
      this.$emit('update:editing', !this.editing)
    },
    toggleShowForm(show) {
      if (this.$breakpoint.desktopAndUp) {
        const notesFormElm = this.$refs.notesForm.$el
        const handler = () => {
          this.resizeFooterHeight()
        }
        const resizeObserver = new ResizeObserver(handler)
        if (show) {
          this.$nextTick(() => {
            if (notesFormElm.querySelector('form')) {
              resizeObserver.observe(notesFormElm.querySelector('form'))
            }
          })
        } else {
          resizeObserver.unobserve(notesFormElm.querySelector('form'))
        }
      }

      this.showForm = show
    },
    // Permet au footer de s'aggrandir selon la hauteur de l'input textarea du formulaire
    resizeFooterHeight() {
      if (!this.$refs.notesForm.$el.querySelector('form')) return
      const notesForm = this.$refs.notesForm.$el.querySelector('form')
      this.footerMinHeight = notesForm.offsetHeight
    }
  }
}
</script>

<style lang="scss" scoped>
@include desktop {
  .document {
    .document-head .level-right {
      opacity: 0;
    }

    &:hover .document-head .level-right {
      opacity: 1;
    }

    &__footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
}
</style>